<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import {
  NEW_PASSWORD_MIN_LENGTH,
  NxBanner,
  NxButton,
  NxLabel,
  NxNewPasswordField,
  NxPasswordInput,
  NxSpinner,
  NxTextInput,
  newPasswordProblem,
} from '@blpde/nx-ui';
import { takeLoginHints } from '@/loginHint';
import { onSupportLinkArrived, takePendingSupportLink, type SupportLink } from '@/supportLink';
import LoginPageFrame from '@/components/LoginPageFrame.vue';
import {
  applyLoginBrandingLook,
  fetchLoginBrandingOf,
  loginBrandingOrigins,
  readCachedLoginBrandingOf,
  type LoginBranding,
} from '@/loginBranding';
import {
  getApiOrigin,
  isPasswordChangeRequired,
  LoginError,
  loadLastEmail,
  login,
  loginWithLink,
  loginMarker,
  loginWithSupportLink,
  onBeforeSignOut,
  normalizeApiBase,
  saveApiBase,
  saveLastEmail,
} from '@/auth';

const { t, te } = useI18n();
const route = useRoute();
const router = useRouter();
// The central login may have sent the person here with an address and a company
// (W18): the address prefills the field (never submits), the company travels
// with the sign-in as a hint. Both are taken out of the address bar at once.
const { loginLink, loginLinkMalformed, ...hints } = takeLoginHints(route, router);
const email = ref(hints.email || loadLastEmail());
const password = ref('');
// A mailed login link (W18 slice 4) is redeemed at once, so the page starts out
// busy: the spinner takes the form's place until master answered.
// A remote support link (Fernwartung, `#support=<id>.<secret>`) was taken out
// of the address before the router started and waits for this view; it is
// redeemed the same way, and at once.
const supportLink = takePendingSupportLink();
const redeeming = ref(Boolean(loginLink || supportLink));
const isLoading = ref(redeeming.value);
const error = ref<string | null>(null);
const formRef = ref<HTMLFormElement | null>(null);

// An account the administrator flagged has to set a new password before it
// may sign in (I-2301): master answers the right password with
// `password_change_required`, and the form turns into the new-password step.
// That step re-sends the same email + current password with the new one. Both
// passwords live only in these refs — nothing of them is ever stored.
type LoginStep = 'credentials' | 'new_password';
const step = ref<LoginStep>('credentials');
const newPassword = ref('');
const newPasswordRepeat = ref('');
const showNewPasswordErrors = ref(false);

// Browser password managers autofill these fields on reload without firing an
// `input` event, so the v-model refs can stay empty while the inputs visibly
// hold credentials. Reading the live DOM value on submit lets an autofilled
// login go through and frees the button from gating on the (stale) refs.
function liveValue(id: string, fallback: string): string {
  const el = formRef.value?.querySelector<HTMLInputElement>(`#${id}`);
  return el ? el.value : fallback;
}

// Server/API origin is editable right here on the login screen (the Settings
// dialog can change it later too). Accepts host / host:port / http://host:port.
const server = ref(getApiOrigin());
const normalizedServer = computed(() => normalizeApiBase(server.value));
const showServerHint = computed(() => {
  const raw = server.value.trim();
  return raw.length > 0 && raw !== normalizedServer.value;
});

function normalizeServerInput() {
  server.value = normalizeApiBase(server.value);
  // Another server is another branding (or none): never keep the old one's.
  if (server.value !== askedServer.value) {
    askedServer.value = server.value;
    const origins = loginBrandingOrigins(server.value, true);
    const cached = readCachedLoginBrandingOf(origins);
    brandingOrigin.value = cached?.origin ?? server.value;
    branding.value = cached?.branding ?? null;
    void loadBranding(origins);
  }
}

// The branding of the hostname this page is served on (W19): master answers it
// before anybody signed in, for the Host of the request — so the page asks its
// own origin first (see `loginBrandingOrigins`) and the server the app was
// given second. The last answer of an origin paints at once, the fresh one
// replaces it; nothing reachable leaves the platform's own page. Sign-in
// methods other than the password have no form here yet, so the page always
// offers the password form.
const askedServer = ref(server.value);
const firstOrigins = loginBrandingOrigins(server.value);
const firstCached = readCachedLoginBrandingOf(firstOrigins);
const brandingOrigin = ref(firstCached?.origin ?? server.value);
const branding = ref<LoginBranding | null>(firstCached?.branding ?? null);

let brandingRequest = 0;

async function loadBranding(origins: readonly string[]) {
  const request = ++brandingRequest;
  const answer = await fetchLoginBrandingOf(origins);
  // The server field moved on while master was answering.
  if (!answer || request !== brandingRequest) return;
  brandingOrigin.value = answer.origin;
  branding.value = answer.branding;
}

onMounted(() => void loadBranding(firstOrigins));

watch(branding, (value) => (value ? applyLoginBrandingLook(value.look) : undefined), { immediate: true });

// Maps a backend error payload ({"error":"invalid_credentials"}) or a network
// failure to a friendly, translated message. Unknown codes fall back to the
// generic login error rather than leaking raw server text to the user.
function resolveErrorMessage(raw: string): string {
  const trimmed = (raw || '').trim();
  if (!trimmed) return t('auth.login_error');
  // Already an i18n key thrown upstream (e.g. 'auth.something').
  if (trimmed.startsWith('auth.')) return te(trimmed) ? t(trimmed) : t('auth.login_error');
  // fetch() rejects with TypeError('Failed to fetch') when the server is down.
  if (/network|fetch|failed to fetch|load failed|typeerror/i.test(trimmed)) {
    return t('auth.errors.network_error');
  }
  // Normalize the code ("email and password required" -> email_and_password_required).
  const codeKey = `auth.errors.${trimmed.toLowerCase().replace(/[^a-z0-9_]+/g, '_')}`;
  return te(codeKey) ? t(codeKey) : t('auth.login_error');
}

// Reload so the shared NxSocket re-handshakes with the fresh bearer + every
// sub-app's api/client.ts picks up the new tokens cleanly.
function finishSignIn() {
  const next = (route.query.redirect as string) || '/';
  window.location.hash = next;
  window.location.reload();
}

// The token is the credential of one sign-in: it lives in these variables until
// it is sent and is never shown, stored or logged. Master answers every link it
// refuses (`invalid_login_link`: unknown, used or expired) the same way; the
// other refusals (`account_inactive`, the external-access 403s) read like the
// password login's. Whatever goes wrong, the person lands on the normal form.
//
// One sign-in runs at a time, whether it is a link or a password (the form, or
// its new-password step): a link that arrives while one is in flight waits, the
// newest one only. When the running sign-in fails the waiting link is redeemed
// next; when it signed in the page is leaving and the link is dropped. Without
// that, the redemption would clear the password under a password login whose
// answer is still coming.
let signInRunning = false;
let signedIn = false;
let waitingLink = '';
// A remote support link that arrived during a sign-in: it waits as well (the newest only)
// and goes first, since it replaces whatever session there is.
let waitingSupport: SupportLink | null = null;
// The login marker the waiting links were queued under. Links queued behind a
// slow sign-in are secrets: they are never redeemed after the login changed
// (another tab signed out or in), however the first request ends.
let queuedMarker: string | null = null;

function forgetQueuedLinks(): void {
  waitingLink = '';
  waitingSupport = null;
}

function succeed() {
  signedIn = true;
  waitingLink = '';
  waitingSupport = null;
  finishSignIn();
}

// Called when a sign-in ended without signing in.
function drainWaitingLink() {
  if (queuedMarker !== loginMarker()) forgetQueuedLinks();
  const support = waitingSupport;
  waitingSupport = null;
  if (support && !signedIn) {
    waitingLink = '';
    void redeemSupportLink(support);
    return;
  }
  const next = waitingLink;
  waitingLink = '';
  if (next && !signedIn) void redeemLoginLink(next);
}

async function redeemLoginLink(first: string) {
  if (signInRunning) {
    waitingLink = first;
    queuedMarker = loginMarker();
    return;
  }
  signInRunning = true;
  let token: string | null = first;
  try {
    while (token) {
      step.value = 'credentials';
      password.value = '';
      redeeming.value = true;
      isLoading.value = true;
      error.value = null;
      try {
        await loginWithLink(token);
        succeed();
        return;
      } catch (err) {
        error.value = resolveErrorMessage(err instanceof Error ? err.message : '');
      }
      if (queuedMarker !== loginMarker()) forgetQueuedLinks();
      token = waitingSupport ? null : waitingLink || null;
      if (!waitingSupport) waitingLink = '';
    }
    redeeming.value = false;
    isLoading.value = false;
  } finally {
    signInRunning = false;
  }
  drainWaitingLink();
}

// The remote support link: one request, no waiting link — master answers
// every link it refuses the same way (401 `invalid_support_link`: unknown, used,
// expired, not approved), so the message does too; only a held-back link (429
// `rate_limited`) and a master that cannot serve it (5xx: `edge_unavailable`
// without the edge link, `authentication_unavailable`) read differently. The
// secret lives in `link` until it is sent.
function supportLinkMessage(err: unknown): string {
  if (err instanceof LoginError) {
    if (err.code === 'session_replaced') return t('auth.errors.session_replaced');
    if (err.status === 429) return t('auth.errors.support_rate_limited');
    if (err.status >= 500) return t('auth.errors.support_unavailable');
    return t('auth.errors.invalid_support_link');
  }
  return resolveErrorMessage(err instanceof Error ? err.message : '');
}

async function redeemSupportLink(link: SupportLink) {
  // One sign-in at a time: the link is not spent while another answer is still
  // coming (a late answer is discarded by the session generation, but the link
  // would already be consumed).
  if (signInRunning) {
    waitingSupport = link;
    queuedMarker = loginMarker();
    return;
  }
  signInRunning = true;
  step.value = 'credentials';
  password.value = '';
  redeeming.value = true;
  isLoading.value = true;
  error.value = null;
  try {
    await loginWithSupportLink(link);
    succeed();
    return;
  } catch (err) {
    error.value = supportLinkMessage(err);
  } finally {
    signInRunning = false;
  }
  redeeming.value = false;
  isLoading.value = false;
  drainWaitingLink();
}

// What a link in the address means, at set-up and whenever the route changes
// under the open page (a second mail link followed in the same tab navigates by
// query only: nothing is set up again). `takeLoginHints` has already taken
// every token out of the address by the time this runs.
function takeIncomingLink(incoming: { loginLink: string; loginLinkMalformed: boolean }) {
  if (incoming.loginLink) void redeemLoginLink(incoming.loginLink);
  else if (incoming.loginLinkMalformed && !signInRunning) {
    error.value = t('auth.errors.invalid_login_link');
  }
}

if (loginLinkMalformed) error.value = t('auth.errors.invalid_login_link');
// A link pasted into the tab while this view is open.
const stopListening = onSupportLinkArrived(() => {
  const link = takePendingSupportLink();
  if (link) void redeemSupportLink(link);
});
onBeforeUnmount(stopListening);

// A sign-out announced in this page, or a change of the shared login marker by
// another tab: nothing queued is redeemed afterwards.
const stopSignOutHook = onBeforeSignOut(forgetQueuedLinks);
const onStorage = (event: StorageEvent) => {
  if (event.key === 'formica.oauth.loginId' || event.key === null) forgetQueuedLinks();
};
window.addEventListener('storage', onStorage);
onBeforeUnmount(() => {
  stopSignOutHook();
  window.removeEventListener('storage', onStorage);
});

onMounted(() => {
  if (supportLink) void redeemSupportLink(supportLink);
  else takeIncomingLink({ loginLink, loginLinkMalformed: false });
});
watch(
  () => route.query.login_link,
  (value) => {
    // Our own scrub of the address ends up here with the parameter gone.
    if (value === undefined) return;
    takeIncomingLink(takeLoginHints(route, router));
  },
);

function enterNewPasswordStep() {
  step.value = 'new_password';
  newPassword.value = '';
  newPasswordRepeat.value = '';
  showNewPasswordErrors.value = false;
  error.value = null;
}

// Back to the plain sign-in form. The current password goes too: it was only
// kept for the new-password step.
function leaveNewPasswordStep(message: string | null = null) {
  step.value = 'credentials';
  password.value = '';
  newPassword.value = '';
  newPasswordRepeat.value = '';
  showNewPasswordErrors.value = false;
  error.value = message;
}

// The new-password step's own wording first (`missing_field` means the new
// password there), then the sign-in messages.
function resolveNewPasswordError(err: unknown): string {
  if (err instanceof LoginError && err.code) {
    const key = `auth.password_change.errors.${err.code}`;
    if (te(key)) return t(key);
  }
  return resolveErrorMessage(err instanceof Error ? err.message : '');
}

async function submitNewPassword() {
  showNewPasswordErrors.value = true;
  const problem = newPasswordProblem(newPassword.value, newPasswordRepeat.value, {
    currentPassword: password.value,
  });
  if (problem) return;
  isLoading.value = true;
  signInRunning = true;
  error.value = null;
  try {
    await login(email.value, password.value, {
      newPassword: newPassword.value,
      ...(hints.company ? { companyHint: hints.company } : {}),
    });
    succeed();
  } catch (err) {
    if (err instanceof LoginError && err.code === 'invalid_credentials') {
      // The current password no longer holds (changed meanwhile); only the
      // sign-in form takes a new one.
      leaveNewPasswordStep(t('auth.password_change.errors.invalid_credentials'));
      return;
    }
    error.value = resolveNewPasswordError(err);
  } finally {
    isLoading.value = false;
    signInRunning = false;
    drainWaitingLink();
  }
}

async function handleSubmit() {
  if (isLoading.value) return;
  if (step.value === 'new_password') {
    await submitNewPassword();
    return;
  }
  // Read the live DOM values (autofill-safe), falling back to the v-model refs.
  const emailValue = liveValue('email', email.value).trim();
  const passwordValue = liveValue('password', password.value);
  const serverValue = liveValue('server', server.value).trim();
  if (!emailValue || !passwordValue || !serverValue) {
    error.value = t('auth.errors.email_and_password_required');
    return;
  }
  // Keep the refs in sync with what we actually submit.
  email.value = emailValue;
  password.value = passwordValue;
  server.value = serverValue;
  isLoading.value = true;
  signInRunning = true;
  error.value = null;
  try {
    // Persist the server URL the user typed (host / host:port / http://… all
    // accepted) before login, then mirror the token into every sub-app key.
    server.value = saveApiBase(serverValue);
    saveLastEmail(emailValue);
    if (hints.company) await login(emailValue, passwordValue, { companyHint: hints.company });
    else await login(emailValue, passwordValue);
    succeed();
  } catch (err) {
    if (isPasswordChangeRequired(err)) {
      enterNewPasswordStep();
      return;
    }
    error.value = resolveErrorMessage(err instanceof Error ? err.message : '');
  } finally {
    isLoading.value = false;
    signInRunning = false;
    drainWaitingLink();
  }
}
</script>

<template>
  <LoginPageFrame
    :branding="branding"
    :origin="brandingOrigin"
    :title="step === 'credentials' ? undefined : $t('auth.password_change.title')"
  >
    <form ref="formRef" class="login-form" @submit.prevent="handleSubmit">
      <div v-if="redeeming" class="login-redeeming">
        <NxSpinner :label="$t('auth.login_link.redeeming')" />
      </div>

      <template v-else-if="step === 'credentials'">
        <NxBanner v-if="error" variant="danger" class="login-error">{{ error }}</NxBanner>

        <NxLabel :text="$t('auth.email')">
          <NxTextInput id="email" v-model="email" />
        </NxLabel>

        <NxLabel :text="$t('auth.password')">
          <NxPasswordInput id="password" v-model="password" />
        </NxLabel>

        <NxLabel :text="$t('auth.server')">
          <NxTextInput
            id="server"
            v-model="server"
            autocomplete="url"
            inputmode="url"
            placeholder="http://host:7680"
            @blur="normalizeServerInput"
          />
          <small v-if="showServerHint" class="login-help">{{ $t('auth.server_hint') }} {{ normalizedServer }}</small>
        </NxLabel>

        <NxButton type="submit" width="full" :disabled="isLoading">
          {{ $t('auth.login') }}
        </NxButton>
      </template>

      <template v-else>
        <NxBanner variant="info">{{ $t('auth.password_change.hint') }}</NxBanner>

        <NxBanner v-if="error" variant="danger" class="login-error">{{ error }}</NxBanner>

        <NxLabel :text="$t('auth.password_change.account')">
          <span class="login-account">{{ email }}</span>
        </NxLabel>
        <!-- Lets a password manager file the new password under this account. -->
        <div hidden>
          <NxTextInput :model-value="email" name="username" autocomplete="username" readonly />
        </div>

        <NxNewPasswordField
          id="new-password"
          v-model="newPassword"
          v-model:repeat="newPasswordRepeat"
          :current-password="password"
          :show-errors="showNewPasswordErrors"
          :disabled="isLoading"
          :label="$t('auth.password_change.new_password')"
          :repeat-label="$t('auth.password_change.repeat_password')"
          :too-short-message="$t('auth.password_change.too_short', { min: NEW_PASSWORD_MIN_LENGTH })"
          :mismatch-message="$t('auth.password_change.mismatch')"
          :unchanged-message="$t('auth.password_change.unchanged')"
        />

        <NxButton type="submit" width="full" :disabled="isLoading">
          {{ $t('auth.password_change.submit') }}
        </NxButton>
        <NxButton
          type="button"
          variant="ghost"
          width="full"
          :disabled="isLoading"
          @click="leaveNewPasswordStep()"
        >
          {{ $t('auth.password_change.back') }}
        </NxButton>
      </template>
    </form>
  </LoginPageFrame>
</template>

<style scoped>
.login-form {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-md);
}

.login-redeeming {
  display: flex;
  justify-content: center;
  padding-block: var(--nxt-space-lg);
}

.login-account {
  font-size: var(--nxt-font-size-sm);
  color: var(--nxt-fg);
  word-break: break-all;
}

.login-help {
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-medium);
  color: var(--nxt-fg-muted);
  line-height: var(--nxt-line-height-normal);
  word-break: break-all;
}
</style>
